import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CustomerFormModal = ({ 
  isOpen = false, 
  onClose, 
  onSave, 
  customer = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    servicePreference: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (customer) {
        setFormData({
          name: customer?.name || '',
          email: customer?.email || '',
          phone: customer?.phone || '',
          servicePreference: customer?.servicePreference || '',
          notes: customer?.notes || ''
        });
      } else {
        setFormData({
          name: '',
          email: '',
          phone: '',
          servicePreference: '',
          notes: ''
        });
      }
      setErrors({});
    }
  }, [isOpen, customer]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      handleClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave({
        ...formData,
        id: customer?.id || Date.now()?.toString(),
        createdAt: customer?.createdAt || new Date()?.toISOString(),
        lastAppointment: customer?.lastAppointment || null,
        totalVisits: customer?.totalVisits || 0
      });
      handleClose();
    } catch (error) {
      setErrors({ submit: 'Failed to save customer. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const serviceOptions = [
    { value: '', label: 'None' },
    { value: 'consultation', label: 'Consultation' },
    { value: 'treatment', label: 'Treatment' },
    { value: 'follow-up', label: 'Follow-up' },
    { value: 'assessment', label: 'Assessment' }
  ];

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 z-modal flex items-start justify-center pt-[8vh] px-4"
      onClick={handleBackdropClick}
    >
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm" />
      
      <div className="relative w-full max-w-2xl bg-card rounded-xl shadow-elevation-5 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 md:px-8 md:py-6 border-b border-border">
          <h2 className="text-xl md:text-2xl lg:text-h3 font-heading font-semibold text-foreground">
            {customer ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-smooth disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 md:p-8 space-y-4 md:space-y-6 max-h-[calc(100vh-24vh)] overflow-y-auto">
          <Input
            label="Full Name"
            type="text"
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            placeholder="Enter customer name"
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <Input
              label="Email Address"
              type="email"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              error={errors?.email}
              placeholder="customer@example.com"
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              value={formData?.phone}
              onChange={(e) => handleChange('phone', e?.target?.value)}
              error={errors?.phone}
              placeholder="+1 (555) 000-0000"
              required
            />
          </div>

          <Select
            label="Service Preference"
            description="Default service type for this customer"
            options={serviceOptions}
            value={formData?.servicePreference}
            onChange={(value) => handleChange('servicePreference', value)}
            placeholder="Select preferred service..."
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              value={formData?.notes}
              onChange={(e) => handleChange('notes', e?.target?.value)}
              placeholder="Add any additional notes about the customer..."
              rows={4}
              className="w-full px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none"
            />
          </div>

          {errors?.submit && (
            <div className="p-4 bg-error/10 border border-error rounded-lg">
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              iconName="Check"
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {customer ? 'Update Customer' : 'Add Customer'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CustomerFormModal;